//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IGallery } from '@apis/galleries/galleriesApiModels';
import { ReelIdType } from '@insights/redux/galleries/insightsGalleriesModel';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import ReelId from '@models/ReelId';
import { isDefined } from '@utils/filterPredicates';

export enum INSIGHTS_ROUTES {
  ANALYTICS = 'instagramAccount',
  CAMPAIGNS = 'campaigns',
  CONTENT_SUPPLY_CHAIN = 'content-supply-chain',
  GALLERIES = 'insights.galleries',
  GALLERIES_DETAIL = 'insights.galleries-detail',
  LIKE2BUY = 'insights.like2buy',
  LIKE2BUY_SUMMARY = 'insights.like2BuySummary',
  LIKE2BUY_V2 = 'insights.like2BuyV2',
  LISTENING = 'listening',
  REVEAL = 'insights.reveal',
  RIGHTS_REQUESTS = 'insights.rights-requests',
  SHOWROOM = 'insights.showroom',
  SUMMARY = 'insights.summary',
  SYNDICATION = 'insights.syndication',
}

export enum GALLERY_DISPLAY_TYPES {
  HOMEPAGE = 'Homepage',
  CATEGORY = 'Category',
  GALLERY = 'Gallery',
  PRODUCT_DETAIL = 'Pdp',
  CAMPAIGN = 'Campaign',
  INSTASHOP = 'Instashop',
  UNKNOWN = 'Unknown',
}

export const LinkOutPages: INSIGHTS_ROUTES[] = [
  INSIGHTS_ROUTES.ANALYTICS,
  INSIGHTS_ROUTES.CAMPAIGNS,
  INSIGHTS_ROUTES.LISTENING,
] as const;

export const isGalleryDisplayType = (reelId?: ReelIdType) =>
  isDefined(reelId) &&
  Object.values(GALLERY_DISPLAY_TYPES).includes(
    reelId as GALLERY_DISPLAY_TYPES,
  );

export const GALLERY_DISPLAY_TYPES_LIST = [
  {
    value: GALLERY_DISPLAY_TYPES.HOMEPAGE,
    label: GALLERY_DISPLAY_TYPES.HOMEPAGE,
  },
  {
    value: GALLERY_DISPLAY_TYPES.CATEGORY,
    label: GALLERY_DISPLAY_TYPES.CATEGORY,
  },
  {
    value: GALLERY_DISPLAY_TYPES.GALLERY,
    label: GALLERY_DISPLAY_TYPES.GALLERY,
  },
  { value: GALLERY_DISPLAY_TYPES.PRODUCT_DETAIL, label: 'Product Detail' },
  {
    value: GALLERY_DISPLAY_TYPES.CAMPAIGN,
    label: GALLERY_DISPLAY_TYPES.CAMPAIGN,
  },
  {
    value: GALLERY_DISPLAY_TYPES.INSTASHOP,
    label: GALLERY_DISPLAY_TYPES.INSTASHOP,
  },
  { value: GALLERY_DISPLAY_TYPES.UNKNOWN, label: 'Other' },
];

export const displayTypeFromContainer = (
  containerType: ExperienceContainerType,
) => {
  switch (containerType) {
    case ExperienceContainerType.HOMEPAGE:
      return GALLERY_DISPLAY_TYPES.HOMEPAGE;
    case ExperienceContainerType.CATEGORY:
      return GALLERY_DISPLAY_TYPES.CATEGORY;
    case ExperienceContainerType.PRODUCT:
      return GALLERY_DISPLAY_TYPES.PRODUCT_DETAIL;
    case ExperienceContainerType.CAMPAIGN:
      return GALLERY_DISPLAY_TYPES.CAMPAIGN;
    case ExperienceContainerType.GALLERY:
      return GALLERY_DISPLAY_TYPES.GALLERY;
    case ExperienceContainerType.INSTASHOP:
      return GALLERY_DISPLAY_TYPES.INSTASHOP;
    default:
      return GALLERY_DISPLAY_TYPES.UNKNOWN;
  }
};

export enum PageType {
  DISPLAY = 'display',
  SOURCE = 'source',
}

export interface IInsightsSubPageBase {
  sref: INSIGHTS_ROUTES;
  title: string;
  params: Record<string, unknown>;
}

export interface IInsightsDisplaySubPage extends IInsightsSubPageBase {
  params: {
    pageType: PageType.DISPLAY;
    pageSource: GALLERY_DISPLAY_TYPES;
  };
}

export interface IInsightsSourceSubPage extends IInsightsSubPageBase {
  params: {
    pageType: PageType.SOURCE;
    pageSource: ReelId;
  };
}

export interface IInsightsLike2BuyV2SubPage extends IInsightsSubPageBase {
  params: {
    storeId: string;
  };
}

export const isGalleriesSubPageParams = (
  params: IInsightsParams,
): params is IInsightsGalleriesParams => {
  return 'pageSource' in (params ?? {});
};

export const isGalleriesSubPage = (
  page: IInsightsSubPage,
): page is IInsightsGalleriesSubPage => {
  return isGalleriesSubPageParams(page.params);
};

export const isLike2BuyV2SubPageParams = (
  params: IInsightsParams,
): params is IInsightsLike2BuyV2SubPage['params'] => {
  return 'storeId' in (params ?? {});
};

export const isLike2BuyV2SubPage = (
  page: IInsightsSubPage,
): page is IInsightsLike2BuyV2SubPage => {
  return isLike2BuyV2SubPageParams(page.params);
};

export type IInsightsGalleriesSubPage =
  | IInsightsDisplaySubPage
  | IInsightsSourceSubPage;

export type IInsightsSubPage =
  | IInsightsGalleriesSubPage
  | IInsightsLike2BuyV2SubPage
  | IInsightsSubPageBase;

export type IInsightsGalleriesParams =
  | IInsightsDisplaySubPage['params']
  | IInsightsSourceSubPage['params'];

export type IInsightsParams =
  | IInsightsGalleriesParams
  | IInsightsLike2BuyV2SubPage['params']
  | IInsightsSubPageBase['params'];

export interface IInsightsSidebarPage {
  state: INSIGHTS_ROUTES;
  title: string;
  subItems?: IInsightsSubPage[];
}

export const getAnalyticsNavParamsForGallery = (gallery: IGallery) => {
  return {
    pageSource: gallery.reelId,
    pageType: PageType.SOURCE,
  };
};

export const getGalleriesSubItems: (
  allGalleries: IGallery[],
) => IInsightsSubPage[] = (allGalleries: IGallery[]) => {
  return allGalleries.map<IInsightsSubPage>((gallery) => {
    const params = getAnalyticsNavParamsForGallery(gallery);
    const subItem: IInsightsSubPage = {
      sref: INSIGHTS_ROUTES.GALLERIES_DETAIL,
      title: gallery.displayName,
      params: params,
    };
    return subItem;
  });
};

export const INSIGHTS_SIDEBAR_PAGES: Record<string, IInsightsSidebarPage> = {
  SUMMARY: {
    state: INSIGHTS_ROUTES.SUMMARY,
    title: 'Summary',
  },
  ANALYTICS: {
    state: INSIGHTS_ROUTES.ANALYTICS,
    title: 'Social Analytics',
  },
  LISTENING: {
    state: INSIGHTS_ROUTES.LISTENING,
    title: 'Social Listening',
  },
  CAMPAIGNS: {
    state: INSIGHTS_ROUTES.CAMPAIGNS,
    title: 'Campaigns',
  },
  CONTENT_SUPPLY_CHAIN: {
    state: INSIGHTS_ROUTES.CONTENT_SUPPLY_CHAIN,
    title: 'Content Supply Chain',
  },
  GALLERIES: {
    state: INSIGHTS_ROUTES.GALLERIES,
    title: 'Galleries',
  },
  LIKE2BUY: {
    state: INSIGHTS_ROUTES.LIKE2BUY,
    title: 'Like2Buy',
  },
  REVEAL: {
    state: INSIGHTS_ROUTES.REVEAL,
    title: 'Reveal',
  },
  RIGHTS_REQUESTS: {
    state: INSIGHTS_ROUTES.RIGHTS_REQUESTS,
    title: 'Rights Requests',
  },
  SHOWROOM: {
    state: INSIGHTS_ROUTES.SHOWROOM,
    title: 'Showroom',
  },
  SYNDICATION: {
    state: INSIGHTS_ROUTES.SYNDICATION,
    title: 'Visual Syndication Network',
  },
};
